import type { ModelTransitionState } from "@gadgetinc/conventions";
import type { Instance } from "@gadgetinc/mobx-quick-tree";
import { GadgetSystemDominionId } from "../../database/DominionLiterals";
import {
  ApplyParamsRecordEffectSpecID,
  CreateRecordEffectSpecID,
  UpdateRecordEffectSpecID,
} from "../../database/behaviour/effects/GadgetEffectLiterals";
import { RequiredValidationId } from "../../database/validations/keys";
import { FieldType } from "../../type-system/FieldType";
import type { SemanticVersionConstraint } from "../../versioning/SemanticVersionConstraint";
import type { ConnectionGlobalActionSpec } from "../utils";
import type { ShopifyApiVersion } from "./ShopifyApiVersion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";
import {
  ShopifyAbortSyncSpecID,
  ShopifyGlobalSyncSpecID,
  ShopifyRequiredValidationKey,
  ShopifyScheduledSyncActionKey,
  ShopifyShopKey,
  ShopifySyncAbortKey,
  ShopifySyncCompleteKey,
  ShopifySyncCompletedKey,
  ShopifySyncDomainKey,
  ShopifySyncErrorDetailsKey,
  ShopifySyncErrorKey,
  ShopifySyncErrorMessageKey,
  ShopifySyncErroredKey,
  ShopifySyncForceKey,
  ShopifySyncGlobalActionKey,
  ShopifySyncKey,
  ShopifySyncLastByKey,
  ShopifySyncLastKey,
  ShopifySyncModelsKey,
  ShopifySyncPriorityKey,
  ShopifySyncRunKey,
  ShopifySyncRunningKey,
  ShopifySyncSinceByKey,
  ShopifySyncSpecID,
  ShopifySyncStartKey,
  ShopifySyncSyncSinceKey,
  ShopifySyncToShopKey,
} from "./keys";

export const ExplicitShopifySyncFrameworkVersion = ">=0.3.1";

export const hasExplicitShopifySync = (hasActionsAsCode: boolean, frameworkVersion: Instance<typeof SemanticVersionConstraint>) => {
  return hasActionsAsCode && frameworkVersion.satisfies(ExplicitShopifySyncFrameworkVersion);
};

export const ShopifyGlobalSync = (): ConnectionGlobalActionSpec => {
  return {
    apiIdentifier: "globalShopifySync",
    key: ShopifySyncGlobalActionKey,
    allowBulkInvocation: false,
    onRunEffects: [
      {
        specID: ShopifyGlobalSyncSpecID,
      },
    ],
  };
};

export const ShopifyScheduledSyncApiIdentifier = "scheduledShopifySync";

export const ShopifyScheduledSync = (): ConnectionGlobalActionSpec => {
  return {
    apiIdentifier: ShopifyScheduledSyncApiIdentifier,
    key: ShopifyScheduledSyncActionKey,
    allowBulkInvocation: false,
  };
};

export const shopifySyncTransitionState: ModelTransitionState = {
  run: {
    to: "running",
  },
  complete: {
    from: "running",
    to: "completed",
  },
  error: {
    from: "running",
    to: "errored",
  },
  abort: {
    from: "running",
    to: "errored",
  },
};

const specs: ConnectionModelSpecVersion = {
  key: ShopifySyncKey,
  "2022-01": {
    name: "Shopify Sync",
    fields: [
      {
        key: ShopifySyncToShopKey,
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
        dominionID: GadgetSystemDominionId,
        contributorConfiguration: {
          [GadgetSystemDominionId]: { isWritableInternally: true, isWritablePublicly: true, storeInColumn: false },
        },
        validations: {
          [ShopifyRequiredValidationKey]: {
            key: ShopifyRequiredValidationKey,
            specID: RequiredValidationId,
          },
        },
      },
      {
        key: ShopifySyncErrorMessageKey,
        name: "Error Message",
        fieldType: FieldType.String,
        dominionID: GadgetSystemDominionId,
        contributorConfiguration: {
          [GadgetSystemDominionId]: { isWritableInternally: true, isWritablePublicly: true, storeInColumn: false },
        },
      },
      {
        key: ShopifySyncErrorDetailsKey,
        name: "Error Details",
        fieldType: FieldType.String,
        dominionID: GadgetSystemDominionId,
        contributorConfiguration: {
          [GadgetSystemDominionId]: { isWritableInternally: true, isWritablePublicly: true, storeInColumn: false },
        },
      },
      {
        key: ShopifySyncDomainKey,
        name: "Domain",
        fieldType: FieldType.String,
        dominionID: GadgetSystemDominionId,
        contributorConfiguration: {
          [GadgetSystemDominionId]: { isWritableInternally: true, isWritablePublicly: true, storeInColumn: false },
        },
        validations: {
          [ShopifyRequiredValidationKey]: {
            key: ShopifyRequiredValidationKey,
            specID: RequiredValidationId,
          },
        },
      },
      {
        key: ShopifySyncSyncSinceKey,
        name: "Sync Since",
        fieldType: FieldType.DateTime,
        dominionID: GadgetSystemDominionId,
        contributorConfiguration: {
          [GadgetSystemDominionId]: {
            isWritableInternally: true,
            isWritablePublicly: true,
            storeInColumn: false,
            description: "DateTime that this sync was run from",
          },
        },
      },
      {
        key: ShopifySyncSinceByKey,
        name: "Sync Since By",
        fieldType: FieldType.Enum,
        configuration: {
          allowMultiple: false,
          options: [{ name: "updated_at" }, { name: "created_at" }],
        },
        dominionID: GadgetSystemDominionId,
        contributorConfiguration: {
          [GadgetSystemDominionId]: {
            isWritableInternally: true,
            isWritablePublicly: true,
            storeInColumn: false,
            description: "Changes sync since datetime to filter by updated at or created at",
          },
        },
      },
      {
        key: ShopifySyncLastKey,
        name: "Sync Last",
        fieldType: FieldType.Number,
        dominionID: GadgetSystemDominionId,
        configuration: {
          decimals: 0,
        },
        contributorConfiguration: {
          [GadgetSystemDominionId]: {
            isWritableInternally: true,
            isWritablePublicly: true,
            storeInColumn: false,
            description: "Number of recent records to sync for each model (for 'Sync Last X' functionality)",
          },
        },
      },
      {
        key: ShopifySyncLastByKey,
        name: "Sync Last By",
        fieldType: FieldType.Enum,
        dominionID: GadgetSystemDominionId,
        configuration: {
          allowMultiple: false,
          allowOther: false,
          options: [
            { name: "created_at", color: "blue" },
            { name: "updated_at", color: "green" },
          ],
        },
        contributorConfiguration: {
          [GadgetSystemDominionId]: {
            isWritableInternally: true,
            isWritablePublicly: true,
            storeInColumn: false,
            description: "Which field to sort by when using 'Sync Last X' functionality (created_at or updated_at)",
          },
        },
      },
      {
        key: ShopifySyncModelsKey,
        name: "Models",
        fieldType: FieldType.JSON,
        dominionID: GadgetSystemDominionId,
        contributorConfiguration: {
          [GadgetSystemDominionId]: {
            isWritableInternally: true,
            isWritablePublicly: true,
            storeInColumn: false,
          },
        },
      },
      {
        key: ShopifySyncForceKey,
        name: "Force",
        fieldType: FieldType.Boolean,
        dominionID: GadgetSystemDominionId,
        configuration: {
          default: false,
        },
        contributorConfiguration: {
          [GadgetSystemDominionId]: {
            isWritableInternally: true,
            isWritablePublicly: true,
            storeInColumn: false,
          },
        },
      },
      {
        key: ShopifySyncPriorityKey,
        name: "Priority",
        fieldType: FieldType.Enum,
        dominionID: GadgetSystemDominionId,
        configuration: {
          allowMultiple: false,
          allowOther: false,
          options: [
            { name: "low", color: "gray" },
            { name: "default", color: "blue" },
            { name: "high", color: "red" },
          ],
        },
        contributorConfiguration: {
          [GadgetSystemDominionId]: {
            isWritableInternally: true,
            isWritablePublicly: true,
            storeInColumn: false,
            description: "Priority level for the sync (low, default, or high)",
          },
        },
      },
    ],
    stateChart: {
      includeCRUD: false,
      initialChildState: ShopifySyncStartKey,
      states: {
        _chart: [
          {
            name: "Start",
            key: ShopifySyncStartKey,
            isRecordBirthPlace: true,
            isUndeleteableSystemState: true,
            boxLayout: { x: 40, y: 80, width: 24, height: 24 },
          },
          {
            name: "Running",
            key: ShopifySyncRunningKey,
            isUndeleteableSystemState: true,
            color: "#56BFCC",
            boxLayout: { x: 120, y: 192, width: 200, height: 96 },
          },
          {
            name: "Completed",
            key: ShopifySyncCompletedKey,
            isUndeleteableSystemState: true,
            color: "#67CA48",
            boxLayout: { x: 480, y: 32, width: 200, height: 96 },
          },
          {
            name: "Errored",
            key: ShopifySyncErroredKey,
            isUndeleteableSystemState: true,
            color: "#DF2222",
            boxLayout: { x: 480, y: 352, width: 200, height: 96 },
          },
        ],
      },
      actions: [
        {
          key: ShopifySyncRunKey,
          name: "Run",
          from: "start",
          to: "running",
          apiTriggerable: true,
          allowActionDeletion: false,
          onRunEffects: [
            {
              specID: ApplyParamsRecordEffectSpecID,
            },
            {
              specID: CreateRecordEffectSpecID,
            },
            {
              specID: ShopifySyncSpecID,
            },
          ],
        },
        {
          key: ShopifySyncCompleteKey,
          name: "Complete",
          from: "running",
          to: "completed",
          apiTriggerable: true,
          allowActionDeletion: false,
          onRunEffects: [
            {
              specID: ApplyParamsRecordEffectSpecID,
            },
            {
              specID: UpdateRecordEffectSpecID,
            },
          ],
        },
        {
          key: ShopifySyncErrorKey,
          name: "Error",
          from: "running",
          to: "errored",
          apiTriggerable: true,
          allowActionDeletion: false,
          onRunEffects: [
            {
              specID: ApplyParamsRecordEffectSpecID,
            },
            {
              specID: UpdateRecordEffectSpecID,
            },
          ],
        },
        {
          key: ShopifySyncAbortKey,
          name: "Abort",
          from: "running",
          to: "errored",
          apiTriggerable: true,
          allowActionDeletion: false,
          onRunEffects: [
            {
              specID: ApplyParamsRecordEffectSpecID,
            },
            {
              specID: ShopifyAbortSyncSpecID,
            },
            {
              specID: UpdateRecordEffectSpecID,
            },
          ],
        },
      ],
    },
    // this is a lie, there are no webhooks for this model
    // but this is how we tell the metadata system to skip this model when generating the sync config
    syncConfig: { type: "webhook_only" } as const,
  },
};

export const ShopifySync = ({
  apiVersion,
  frameworkVersionConstraint,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint: string;
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint);
